// ── Procedural Audio for DIG DEEP ──
// All sounds synthesized via Web Audio API — no audio files needed.

export class AudioManager {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private ambientSource: OscillatorNode | null = null;
  private ambientGain: GainNode | null = null;
  private ambientLfo: OscillatorNode | null = null;
  muted: boolean = false;
  volume: number = 0.5;

  /** Create AudioContext on first user interaction. */
  init(): void {
    if (this.ctx) return;
    this.ctx = new AudioContext();
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = this.volume;
    this.masterGain.connect(this.ctx.destination);
  }

  // ── Sound Effects ─────────────────────────────────────────────

  /** Pick: sharp metallic clink. Brush: soft swish. Trowel: scrape. */
  playDig(toolType: string): void {
    const ctx = this.ensureContext();
    if (!ctx) return;

    switch (toolType) {
      case 'pickaxe': {
        // Sharp metallic clink — short triangle wave at 800Hz with fast decay
        this.playTone(800, 0.08, 'triangle', 0.4);
        // Add a higher harmonic for metallic shimmer
        setTimeout(() => this.playTone(1600, 0.05, 'triangle', 0.15), 10);
        break;
      }
      case 'brush': {
        // Soft swish — filtered white noise
        this.playNoise(0.25, 0.15);
        break;
      }
      case 'trowel': {
        // Scraping sound — sawtooth at 400Hz with medium decay
        this.playTone(400, 0.15, 'sawtooth', 0.2);
        // Add a subtle low rumble
        this.playTone(150, 0.12, 'sawtooth', 0.08);
        break;
      }
      default: {
        // Generic dig sound
        this.playTone(600, 0.1, 'triangle', 0.3);
        break;
      }
    }
  }

  /** Ascending chime — more dramatic for rarer finds. */
  playDiscovery(rarity: string): void {
    const ctx = this.ensureContext();
    if (!ctx) return;

    // Note frequencies: C5=523, E5=659, G5=784, C6=1047, E6=1319, C7=2093
    const noteMap: Record<string, number[]> = {
      common:    [523, 659, 784],
      uncommon:  [523, 659, 784, 1047],
      rare:      [523, 659, 784, 1047, 1319],
      legendary: [523, 659, 784, 1047, 1319, 1568, 2093],
      mythical:  [523, 659, 784, 1047, 1319, 1568, 2093, 2637],
    };

    const notes = noteMap[rarity] || noteMap.common;
    const noteSpacing = 100; // ms between notes
    const noteDuration = 0.25;

    notes.forEach((freq, i) => {
      setTimeout(() => {
        this.playTone(freq, noteDuration, 'sine', 0.25);
      }, i * noteSpacing);
    });
  }

  /** Short UI click. */
  playUIClick(): void {
    this.playTone(1000, 0.05, 'sine', 0.15);
  }

  /** Fanfare — ascending C4-E4-G4-C5 arpeggio. */
  playStoryReveal(): void {
    const ctx = this.ensureContext();
    if (!ctx) return;

    // C4=262, E4=330, G4=392, C5=523
    const notes = [262, 330, 392, 523];
    const spacing = 150;

    notes.forEach((freq, i) => {
      setTimeout(() => {
        this.playTone(freq, 0.35, 'sine', 0.3);
      }, i * spacing);
    });
  }

  /** Triumphant major chord. */
  playTheoryCorrect(): void {
    const ctx = this.ensureContext();
    if (!ctx) return;

    // Play a bright major chord: C4, E4, G4, C5 simultaneously
    this.playTone(262, 0.6, 'sine', 0.2);
    this.playTone(330, 0.6, 'sine', 0.2);
    this.playTone(392, 0.6, 'sine', 0.2);
    this.playTone(523, 0.6, 'sine', 0.15);

    // Follow with a higher octave flourish
    setTimeout(() => {
      this.playTone(523, 0.3, 'sine', 0.2);
      this.playTone(659, 0.3, 'sine', 0.2);
      this.playTone(784, 0.3, 'sine', 0.2);
      this.playTone(1047, 0.4, 'sine', 0.15);
    }, 400);
  }

  /** Descending sad tone. */
  playTheoryWrong(): void {
    const ctx = this.ensureContext();
    if (!ctx) return;

    // Descending minor: E4, C4, A3, F3
    const notes = [330, 262, 220, 175];
    const spacing = 180;

    notes.forEach((freq, i) => {
      setTimeout(() => {
        this.playTone(freq, 0.3, 'sine', 0.25);
      }, i * spacing);
    });
  }

  /** Cash register cha-ching. */
  playSell(): void {
    const ctx = this.ensureContext();
    if (!ctx) return;

    // Initial "cha" — short burst of noise
    this.playNoise(0.06, 0.3);

    // "Ching" — high bell-like tone
    setTimeout(() => {
      this.playTone(2200, 0.15, 'sine', 0.3);
      this.playTone(3300, 0.12, 'sine', 0.15);
    }, 80);

    // Second ring slightly higher
    setTimeout(() => {
      this.playTone(2600, 0.2, 'sine', 0.25);
      this.playTone(3900, 0.15, 'sine', 0.12);
    }, 200);
  }

  /** Gentle wind-down tone. */
  playDayEnd(): void {
    const ctx = this.ensureContext();
    if (!ctx) return;

    // Soft descending tones: G4, E4, C4
    const notes = [392, 330, 262];
    const spacing = 300;

    notes.forEach((freq, i) => {
      setTimeout(() => {
        this.playTone(freq, 0.5, 'sine', 0.2);
      }, i * spacing);
    });
  }

  /** Weather transition sounds. */
  playWeatherChange(weather: string): void {
    const ctx = this.ensureContext();
    if (!ctx) return;

    switch (weather) {
      case 'rain': {
        // Short burst of filtered noise like rain starting
        this.playNoise(0.8, 0.2);
        break;
      }
      case 'storm': {
        // Deep rumble — low frequency
        this.playTone(50, 1.0, 'sawtooth', 0.25);
        this.playNoise(0.6, 0.15);
        break;
      }
      case 'heat_wave': {
        // Shimmering high tone
        this.playTone(1200, 0.4, 'sine', 0.1);
        setTimeout(() => this.playTone(1250, 0.4, 'sine', 0.1), 200);
        break;
      }
      case 'perfect': {
        // Bright pleasant chime
        this.playTone(784, 0.3, 'sine', 0.2);
        setTimeout(() => this.playTone(1047, 0.3, 'sine', 0.2), 150);
        break;
      }
      case 'clear':
      default: {
        // Gentle neutral tone
        this.playTone(440, 0.3, 'sine', 0.15);
        break;
      }
    }
  }

  // ── Ambient Sounds ────────────────────────────────────────────

  /** Start continuous ambient background based on site type. */
  startAmbient(siteType: string): void {
    const ctx = this.ensureContext();
    if (!ctx) return;

    // Stop any existing ambient first
    this.stopAmbient();

    // Site-specific ambient configuration
    const ambientConfig: Record<string, { freq: number; type: OscillatorType; lfoFreq: number; vol: number }> = {
      desert:     { freq: 70,  type: 'sine',     lfoFreq: 0.3,  vol: 0.06 },
      jungle:     { freq: 90,  type: 'triangle',  lfoFreq: 0.8,  vol: 0.07 },
      underwater: { freq: 60,  type: 'sine',     lfoFreq: 0.15, vol: 0.08 },
      arctic:     { freq: 80,  type: 'sine',     lfoFreq: 0.2,  vol: 0.05 },
      ruins:      { freq: 75,  type: 'triangle',  lfoFreq: 0.4,  vol: 0.06 },
      cave:       { freq: 65,  type: 'sine',     lfoFreq: 0.1,  vol: 0.07 },
      default:    { freq: 80,  type: 'sine',     lfoFreq: 0.25, vol: 0.06 },
    };

    const config = ambientConfig[siteType] || ambientConfig.default;

    // Create ambient oscillator
    this.ambientSource = ctx.createOscillator();
    this.ambientSource.type = config.type;
    this.ambientSource.frequency.value = config.freq;

    // Create gain for ambient volume
    this.ambientGain = ctx.createGain();
    this.ambientGain.gain.value = config.vol;

    // Create LFO for gentle modulation
    this.ambientLfo = ctx.createOscillator();
    this.ambientLfo.type = 'sine';
    this.ambientLfo.frequency.value = config.lfoFreq;

    const lfoGain = ctx.createGain();
    lfoGain.gain.value = config.freq * 0.05; // Subtle pitch modulation

    // Connect LFO to oscillator frequency
    this.ambientLfo.connect(lfoGain);
    lfoGain.connect(this.ambientSource.frequency);

    // Connect ambient chain
    this.ambientSource.connect(this.ambientGain);
    this.ambientGain.connect(this.masterGain!);

    // Start both oscillators
    this.ambientSource.start();
    this.ambientLfo.start();
  }

  /** Stop ambient sounds. */
  stopAmbient(): void {
    if (this.ambientSource) {
      try { this.ambientSource.stop(); } catch (_) { /* already stopped */ }
      this.ambientSource.disconnect();
      this.ambientSource = null;
    }
    if (this.ambientLfo) {
      try { this.ambientLfo.stop(); } catch (_) { /* already stopped */ }
      this.ambientLfo.disconnect();
      this.ambientLfo = null;
    }
    if (this.ambientGain) {
      this.ambientGain.disconnect();
      this.ambientGain = null;
    }
  }

  // ── Controls ──────────────────────────────────────────────────

  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.masterGain) {
      this.masterGain.gain.value = this.muted ? 0 : this.volume;
    }
  }

  toggleMute(): void {
    this.muted = !this.muted;
    if (this.masterGain) {
      this.masterGain.gain.value = this.muted ? 0 : this.volume;
    }
  }

  // ── Private Helpers ───────────────────────────────────────────

  /** Ensure AudioContext is ready; resume if suspended. Returns null if unavailable. */
  private ensureContext(): AudioContext | null {
    if (!this.ctx) {
      this.init();
    }
    if (!this.ctx || !this.masterGain) return null;
    if (this.muted) return null;

    // Resume suspended context (browsers require user gesture)
    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }

    return this.ctx;
  }

  /** Play a single tone with attack/decay envelope. */
  private playTone(
    freq: number,
    duration: number,
    type: OscillatorType = 'sine',
    volume: number = 0.3
  ): void {
    const ctx = this.ctx;
    if (!ctx || !this.masterGain) return;

    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.value = freq;

    const gain = ctx.createGain();
    const now = ctx.currentTime;

    // Envelope: quick attack, sustain, then decay
    const attackTime = 0.01;
    const decayStart = duration * 0.3;
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(volume, now + attackTime);
    gain.gain.setValueAtTime(volume, now + decayStart);
    gain.gain.exponentialRampToValueAtTime(0.001, now + duration);

    osc.connect(gain);
    gain.connect(this.masterGain);

    osc.start(now);
    osc.stop(now + duration + 0.01);

    // Clean up after playback
    osc.onended = () => {
      osc.disconnect();
      gain.disconnect();
    };
  }

  /** Play filtered white noise for brush/rain effects. */
  private playNoise(duration: number, volume: number = 0.2): void {
    const ctx = this.ctx;
    if (!ctx || !this.masterGain) return;

    // Generate white noise buffer
    const bufferSize = ctx.sampleRate * duration;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    const source = ctx.createBufferSource();
    source.buffer = buffer;

    // Bandpass filter to soften the noise
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 3000;
    filter.Q.value = 0.5;

    const gain = ctx.createGain();
    const now = ctx.currentTime;

    // Envelope: fade in, then fade out
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(volume, now + 0.02);
    gain.gain.setValueAtTime(volume, now + duration * 0.5);
    gain.gain.exponentialRampToValueAtTime(0.001, now + duration);

    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);

    source.start(now);
    source.stop(now + duration + 0.01);

    source.onended = () => {
      source.disconnect();
      filter.disconnect();
      gain.disconnect();
    };
  }
}

/** Singleton audio manager instance. */
export const audioManager = new AudioManager();
